import {
  Gift,
  Gamepad2,
} from "lucide-react";
import React, { useState, useMemo } from "react";
import toast from "react-hot-toast";
import { ApiError } from "../../store/utils/apiError";
import {
  useGetCompOffListQuery,
  useApproveCompOffMutation,
  useRejectCompOffMutation,
} from "../../store/apis/compOff.api";
import {
  Badge,
  AlertModal,
  PageHeader,
  ApproveRejectActions,
  TabSelector
} from "../common";
import ApproveModal from "./component/ApproveModal";
import { getAvatarGradient } from "../../utils/avatarUtils";
import type { BadgeVariant } from "../common/Badge";
import { DataTable, DataTableCard } from "../common/Table2";
import type { TableColumn } from "../common/Table";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import { useAuth } from "../../store/hooks/useAuth";
import { COMP_OFF_APPROVED_STATUSES, COMP_OFF_PENDING_STATUSES, COMP_OFF_REJECTED_STATUSES, HEADER_CLS } from "../../utils/constants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import RejectionModal from "../leave/modals/RejectionModal";
import { getWorkflowStatusVariant } from "../../utils/badgeVariants";
import { capitalizeWords } from "../../utils/nameUtils";
import { useGetBookingListQuery, usePerformBookingActionMutation } from "../../store/apis/gameBooking.api";
import type { GameBookingItem } from "../../store/apis/gameBooking.api";
import type { CompOffItem } from "../../store/apis/compOff.api";
import { WorkflowQueryStatus } from "../../utils/constants";
import { formatDateDDMMM, formatDateDDMMMYYYY, formatTimeOnly } from "../../utils/timeUtils";

dayjs.extend(utc);
dayjs.extend(timezone);

// Page cap used for each source list. The unified table merges both sources
// client-side, so counts shown in the UI come from the API totals instead of
// the merged array length.
const MAX_LIST_SIZE = 100;

export enum ApprovalType {
  COMP_OFF = "Comp-Off",
  GAME_BOOKING = "Game-Booking",
}

interface UnifiedApprovalItem {
  id: string;
  employeeName: string;
  type: ApprovalType;
  details: string;
  status: string;
  statusLabel?: string;
  createdAt: string;
  originalItem: CompOffItem | GameBookingItem;
}

const formatRelativeDate = (dateStr: string, timezone: string) => {
  const d = dayjs(dateStr).tz(timezone);
  const today = dayjs().tz(timezone).startOf("day");
  const tomorrow = today.add(1, "day");
  const yesterday = today.subtract(1, "day");

  if (d.isSame(today, "d")) {
    return "Today";
  } else if (d.isSame(tomorrow, "d")) {
    return "Tomorrow";
  } else if (d.isSame(yesterday, "d")) {
    return "Yesterday";
  } else {
    return formatDateDDMMM(dateStr, timezone);
  }
};

const mapCompOffItem = (
  item: CompOffItem,
  userTimezone: string,
): UnifiedApprovalItem => ({
  id: item.id,
  employeeName: item.fullName || "Unknown User",
  type: ApprovalType.COMP_OFF,
  details: `${formatDateDDMMMYYYY(item.leaveDate)} · ${item.isFirstHalf ? "First half" : item.isSecondHalf ? "Second half" : "Full day"} · ${item.comment || ""}`,
  status: item.status,
  statusLabel: item.statusLabel,
  createdAt: item.createdAt,
  originalItem: item,
});

const mapBookingItem = (
  item: GameBookingItem,
  userTimezone: string,
): UnifiedApprovalItem => ({
  id: item.id,
  employeeName: item.creatorName || "Unknown User",
  type: ApprovalType.GAME_BOOKING,
  details: `${item.gameName} · ${formatRelativeDate(item.startTime, userTimezone)} ${formatTimeOnly(item.startTime, userTimezone)} - ${formatTimeOnly(item.endTime, userTimezone)}`,
  status: item.status,
  statusLabel: item.statusLabel,
  createdAt: item.createdAt,
  originalItem: item,
});

const EmployeeSelfService: React.FC = () => {
  const userTimezone = dayjs.tz.guess();
  const { user } = useAuth();
  const canActOnApprovals = useHasPermission(
    PERMISSIONS.EMPLOYEE_SERVICE_MANAGE,
  );

  const [activeTab, setActiveTab] = useState<"pending" | "history">("pending");
  const [processing, setProcessing] = useState<string | null>(null);

  // Approval confirmation modal state
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [approveTarget, setApproveTarget] = useState<UnifiedApprovalItem | null>(null);

  // Rejection modal state
  const [rejectTarget, setRejectTarget] = useState<UnifiedApprovalItem | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);

  // --- API Queries ---
  const {
    data: pendingCompOffResponse,
    isLoading: pendingCompOffLoading,
    isFetching: pendingCompOffFetching,
    isError: pendingCompOffError,
    refetch: refetchPendingCompOff,
  } = useGetCompOffListQuery({
    status: COMP_OFF_PENDING_STATUSES,
    page: 1,
    limit: MAX_LIST_SIZE,
  });

  const {
    data: pendingBookingResponse,
    isLoading: pendingBookingLoading,
    isFetching: pendingBookingFetching,
    isError: pendingBookingError,
    refetch: refetchPendingBooking,
  } = useGetBookingListQuery({
    status: WorkflowQueryStatus.PENDING,
    page: 1,
    limit: MAX_LIST_SIZE,
  });

  const {
    data: historyCompOffResponse,
    isLoading: historyCompOffLoading,
    isFetching: historyCompOffFetching,
    isError: historyCompOffError,
    refetch: refetchHistoryCompOff,
  } = useGetCompOffListQuery({
    status: `${COMP_OFF_APPROVED_STATUSES},${COMP_OFF_REJECTED_STATUSES}`,
    page: 1,
    limit: MAX_LIST_SIZE,
  });

  const {
    data: historyBookingResponse,
    isLoading: historyBookingLoading,
    isFetching: historyBookingFetching,
    isError: historyBookingError,
    refetch: refetchHistoryBooking,
  } = useGetBookingListQuery({
    status: WorkflowQueryStatus.HISTORY,
    page: 1,
    limit: MAX_LIST_SIZE,
  });

  // Mutations
  const [approveCompOff] = useApproveCompOffMutation();
  const [rejectCompOff] = useRejectCompOffMutation();
  const [performBookingAction] = usePerformBookingActionMutation();

  // --- Combined Lists ---
  const pendingList = useMemo<UnifiedApprovalItem[]>(() => {
    const list: UnifiedApprovalItem[] = [
      ...(pendingCompOffResponse?.data ?? []).map((item) =>
        mapCompOffItem(item, userTimezone),
      ),
      ...(pendingBookingResponse?.data ?? []).map((item) =>
        mapBookingItem(item, userTimezone),
      ),
    ];

    return list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [pendingCompOffResponse, pendingBookingResponse, userTimezone]);

  const historyList = useMemo<UnifiedApprovalItem[]>(() => {
    const list: UnifiedApprovalItem[] = [
      ...(historyCompOffResponse?.data ?? []).map((item) =>
        mapCompOffItem(item, userTimezone),
      ),
      ...(historyBookingResponse?.data ?? []).map((item) =>
        mapBookingItem(item, userTimezone),
      ),
    ];

    return list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [historyCompOffResponse, historyBookingResponse, userTimezone]);

  const activeList = activeTab === "pending" ? pendingList : historyList;
  const pendingTotalCount =
    (pendingCompOffResponse?.total ?? 0) + (pendingBookingResponse?.total ?? 0);
  const historyTotalCount =
    (historyCompOffResponse?.total ?? 0) + (historyBookingResponse?.total ?? 0);
  const totalCount =
    activeTab === "pending" ? pendingTotalCount : historyTotalCount;

  const isLoading = activeTab === "pending"
    ? pendingCompOffLoading || pendingBookingLoading
    : historyCompOffLoading || historyBookingLoading;

  const isFetching = activeTab === "pending"
    ? pendingCompOffFetching || pendingBookingFetching
    : historyCompOffFetching || historyBookingFetching;

  const listError = activeTab === "pending"
    ? pendingCompOffError || pendingBookingError
    : historyCompOffError || historyBookingError;

  const retryListFetch = () => {
    if (activeTab === "pending") {
      if (pendingCompOffError) refetchPendingCompOff();
      if (pendingBookingError) refetchPendingBooking();
    } else {
      if (historyCompOffError) refetchHistoryCompOff();
      if (historyBookingError) refetchHistoryBooking();
    }
  };

  const handleApproveClick = (item: UnifiedApprovalItem) => {
    if (!canActOnApprovals) {
      toast.error("You do not have permission to approve requests.");
      return;
    }
    setApproveTarget(item);
    setShowApproveConfirm(true);
  };

  const confirmApprove = async (item: UnifiedApprovalItem) => {
    try {
      setProcessing(item.id);
      if (item.type === "Comp-Off") {
        await approveCompOff({ id: item.id, actorId: user?.id || "" }).unwrap();
        toast.success("Comp-off request approved successfully.");
      } else {
        await performBookingAction({ id: item.id, decision: "approved" }).unwrap();
        toast.success("Game booking approved successfully.");
      }
    } catch (error: unknown) {
      const err = error as ApiError;
      const rawMessage = err?.data?.message || err?.message || "Failed to approve request.";
      const errorMessage = Array.isArray(rawMessage) ? rawMessage.join(", ") : rawMessage;
      toast.error(errorMessage);
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectItem = (item: UnifiedApprovalItem) => {
    if (!canActOnApprovals) {
      toast.error("You do not have permission to reject requests.");
      return;
    }
    setRejectTarget(item);
    setRejectionReason("");
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    if (!rejectTarget || !rejectionReason.trim()) return;
    try {
      setProcessing(rejectTarget.id);
      if (rejectTarget.type === ApprovalType.COMP_OFF) {
        await rejectCompOff({
          id: rejectTarget.id,
          actorId: user?.id || "",
          reason: rejectionReason,
        }).unwrap();
        toast.success("Comp-off request rejected successfully.");
      } else {
        await performBookingAction({
          id: rejectTarget.id,
          decision: "rejected",
          remarks: rejectionReason,
        }).unwrap();
        toast.success("Game booking rejected successfully.");
      }
      setShowRejectModal(false);
      setRejectionReason("");
      setRejectTarget(null);
    } catch (error: unknown) {
      const err = error as ApiError;
      const rawMessage = err?.data?.message || err?.message || "Failed to reject request.";
      const errorMessage = Array.isArray(rawMessage) ? rawMessage.join(", ") : rawMessage;
      toast.error(errorMessage);
    } finally {
      setProcessing(null);
    }
  };

  // --- Table Columns ---
  const columns = useMemo<TableColumn<UnifiedApprovalItem>[]>(() => [
    {
      key: "employee",
      title: "EMPLOYEE",
      label: "Employee",
      required: true,
      headerClassName: HEADER_CLS,
      render: (_, record) => (
        <div className="flex items-center gap-[11px]">
          <div
            className="w-[38px] h-[38px] rounded-button flex-none grid place-items-center text-white font-bold text-xs"
            style={{ background: getAvatarGradient(record.employeeName || "") }}
          >
            {(record.employeeName?.charAt(0) || "?").toUpperCase()}
          </div>
          <span className="text-sm font-semibold text-slate-900 ml-3 capitalize">
            {record.employeeName}
          </span>
        </div>
      ),
    },
    {
      key: "type",
      title: "TYPE",
      label: "Type",
      required: true,
      headerClassName: HEADER_CLS,
      render: (_, record) => {
        if (record.type === ApprovalType.COMP_OFF) {
          return (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#F5F3FF] text-[#6D28D9] border border-[#DDD6FE]">
              <Gift className="w-3.5 h-3.5" />
              Comp-Off
            </span>
          );
        } else {
          return (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#EFF6FF] text-[#2563EB] border border-[#DBEAFE]">
              <Gamepad2 className="w-3.5 h-3.5" />
              Game Booking
            </span>
          );
        }
      },
    },
    {
      key: "details",
      title: "DETAILS",
      label: "Details",
      headerClassName: HEADER_CLS,
      render: (_, record) => (
        <span className="text-sm text-slate-600 block">
          {record.details}
        </span>
      ),
    },
    {
      key: "status",
      title: "STATUS",
      label: "Status",
      headerClassName: HEADER_CLS,
      render: (_, record) => (
        <Badge
          variant={getWorkflowStatusVariant(record.status)}
          size="middle"
        >
          {record.statusLabel ?? capitalizeWords(record.status)}
        </Badge>
      ),
    },
    {
      key: "action",
      title: "ACTION",
      label: "Action",
      required: true,
      align: "right",
      headerClassName: HEADER_CLS,
      render: (_, record) => {
        if (activeTab !== "pending") return null;
        if (!canActOnApprovals) {
          return null;
        }
        return (
          <ApproveRejectActions
            onApprove={() => handleApproveClick(record)}
            onReject={() => handleRejectItem(record)}
            loading={processing === record.id}
            disabled={!!processing}
            rejectIconOnly={true}
            approveIconOnly={false}
            size="sm"
          />
        );
      },
    },
  ], [activeTab, processing, canActOnApprovals]);

  const mainTabs = useMemo(() => [
    { k: "pending", label: "Pending", count: pendingTotalCount },
    { k: "history", label: "History", count: historyTotalCount },
  ], [pendingTotalCount, historyTotalCount]);

  return (
    <>
      <div>
        <div className="max-w-7xl mx-auto space-y-4">
          <PageHeader
            className="!mb-2 sm:!mb-3"
            title="Self-Service Approvals"
            description="Review comp-off requests and game room bookings"
            breadcrumbs={[{ label: "Home" }, { label: "Self-Service Approvals" }]}
          />

          <TabSelector
            variant="segmented"
            size="md"
            tabs={mainTabs}
            active={activeTab}
            onChange={(tab) => {
              setActiveTab(tab as "pending" | "history");
            }}
            className="mb-5"
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-6">
      {listError && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 mb-4">
          <span className="text-sm text-red-700">
            Failed to load {activeTab} approvals. Some data may be missing.
          </span>
          <button
            type="button"
            onClick={retryListFetch}
            className="text-sm font-semibold text-red-700 underline hover:text-red-800"
          >
            Retry
          </button>
        </div>
      )}

      <DataTableCard>
        <DataTable<UnifiedApprovalItem>
          columns={columns}
          rows={activeList}
          loading={isLoading || isFetching}
          emptyMessage={listError ? "Failed to load approvals" : activeTab === "pending" ? "No pending approvals found" : "No approval history found"}
          rowKey={(r) => r.id}
        />
        {!isLoading && !isFetching && !listError && totalCount > activeList.length && (
          <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
            Showing the latest {activeList.length} of {totalCount} requests. Older
            entries beyond {MAX_LIST_SIZE} per source are not listed here.
          </div>
        )}
      </DataTableCard>

      {/* Approve Confirmation Modal */}
      <ApproveModal
        isOpen={showApproveConfirm}
        onClose={() => {
          setShowApproveConfirm(false);
          setApproveTarget(null);
        }}
        onConfirm={() => {
          if (approveTarget) {
            confirmApprove(approveTarget);
          }
          setShowApproveConfirm(false);
          setApproveTarget(null);
        }}
        title="Approve Request"
        message={`Are you sure you want to approve this ${approveTarget?.type === ApprovalType.COMP_OFF ? "comp-off" : "game booking"} request?`}
        confirmText="Approve"
        processing={!!processing}
      />

      {/* Unified Rejection Modal */}
      {showRejectModal && rejectTarget && (
        <RejectionModal
          rejectionReason={rejectionReason}
          selectedLeave={rejectTarget.id}
          confirmReject={confirmReject}
          processing={processing === rejectTarget.id}
          setRejectionReason={setRejectionReason}
          setSelectedLeave={() => setRejectTarget(null)}
          setShowRejectModal={setShowRejectModal}
          title={rejectTarget.type === ApprovalType.COMP_OFF ? "Reject Comp-Off Request" : "Reject Game Booking"}
          description={rejectTarget.type === ApprovalType.COMP_OFF ? "Please provide a reason for rejecting this comp-off entry." : "Please provide a reason for rejecting this game booking."}
        />
      )}
    </div>
    </>
  );
};

export default EmployeeSelfService;
